import React, { useState, useEffect } from 'react';
import { MdDownload } from 'react-icons/md';
import BBITable from '../../../helpers/bBITable/BBITable';
import DateOnlyFormatter from '../../../helpers/inputs/DateOnlyFormatter';
import FilterData from '../../../helpers/filter/FilterData';
import Get from '../../../api/internal/Get';
import Header from '../../../layouts/Header';
import View from '../../../helpers/slab/View';
import ViewCustomer from '../../../features/views/customer/Customer';
import ViewLoad from '../../../features/views/load/Load';
import ViewRental from '../../../features/views/rental/Rental';
import ViewReceivable from '../../../features/views/receivable/Receivable';
import { useOutletContext } from 'react-router-dom';
import { CSVLink } from 'react-csv';

function OutstandingReceivablesBoard({
  toggleBoardRefresh,
}: {
  toggleBoardRefresh: boolean;
}) {
  const [receivableBoard, setReceivableBoard] = useState<
    Array<ReceivableBoard>
  >([]);
  const filterValue = useOutletContext();

  useEffect(() => {
    Get(`/Accounting/GetReceivableBoard/`).then(response => {
      if (response) {
        setReceivableBoard(FormatReceivables(response.data));
      }
    });
  }, [toggleBoardRefresh]);

  function FormatReceivables(receivables) {
    return receivables?.map(receivable => ({
      ...receivable,
      attribute: receivable.loadId
        ? receivable.loadId
        : receivable.rentalId && 'Rental: ' + receivable.rentalId,
      dueDate: DateOnlyFormatter(receivable.dueDate),
      invoiceDate: DateOnlyFormatter(receivable.invoiceDate),
      remainingBalance: receivable.remainingBalance.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
      total: receivable.total.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
    }));
  }

  const tableData = FilterData(FormatReceivables(receivableBoard), filterValue);

  const columnConfig = [
    {
      key: '1',
      attribute: 'attribute',
      attributeprimaryhidden: 'loadId',
      attributesecondaryhidden: 'rentalId',
      header: 'Load #',
      onClick: e =>
        View(
          e.target.dataset.attributeprimaryhidden ? (
            <ViewLoad loadId={e.target.dataset.attributeprimaryhidden} />
          ) : (
            e.target.dataset.attributesecondaryhidden && (
              <ViewRental
                rentalId={e.target.dataset.attributesecondaryhidden}
              />
            )
          ),
        ),
      dataType: 'number',
    },
    {
      key: '2',
      attribute: 'reference',
      attributeprimaryhidden: 'loadId',
      attributesecondaryhidden: 'rentalId',
      header: 'Reference',
      onClick: e =>
        View(
          e.target.dataset.attributeprimaryhidden ? (
            <ViewLoad loadId={e.target.dataset.attributeprimaryhidden} />
          ) : (
            e.target.dataset.attributesecondaryhidden && (
              <ViewRental
                rentalId={e.target.dataset.attributesecondaryhidden}
              />
            )
          ),
        ),
    },
    {
      key: '3',
      attribute: 'paymentStatus',
      attributeprimaryhidden: 'customerAssignmentId',
      attributesecondaryhidden: 'loadId',
      attributetertiaryhidden: 'rentalId',
      header: 'Status',
      onClick: e =>
        View(
          <ViewReceivable
            customerAssignmentId={e.target.dataset.attributeprimaryhidden}
            loadId={e.target.dataset.attributesecondaryhidden}
            rentalId={e.target.dataset.attributetertiaryhidden}
          />,
        ),
      dataType: 'string',
    },
    {
      key: '4',
      attribute: 'customerName',
      attributeprimaryhidden: 'customerId',
      header: 'Customer',
      onClick: e =>
        View(
          <ViewCustomer
            customerName={e.target.innerText}
            customerId={e.target.dataset.attributeprimaryhidden}
          />,
        ),
    },
    {
      key: '5',
      attribute: 'quickBooksInvoiceId',
      header: 'QuickBooks ID',
      dataType: 'number',
    },
    {
      key: '6',
      attribute: 'branchName',
      header: 'Branch',
    },
    {
      key: '7',
      attribute: 'invoiceDate',
      header: 'Invoice Date',
      dataType: 'date',
    },
    {
      key: '8',
      attribute: 'dueDate',
      header: 'Due Date',
      dataType: 'date',
    },
    {
      key: '9',
      attribute: 'total',
      header: 'Total',
      dataType: 'currency',
      includeInTotal: true,
    },
    {
      key: '10',
      attribute: 'remainingBalance',
      header: 'Remaining Balance',
      dataType: 'currency',
      includeInTotal: true,
    },
  ];

  const csvFileName =
    new Date().toISOString().split('T')[0].replace(/[-]/g, '') +
    '_Receivables Report.csv';

  const ReceivableTable = () => {
    return <BBITable data={tableData} columns={columnConfig} />;
  };

  return (
    <>
      <Header>
        <button>
          <CSVLink
            data={tableData}
            headers={[
              { label: 'Load #', key: 'attribute' },
              { label: 'Reference', key: 'reference' },
              { label: 'Status', key: 'paymentStatus' },
              { label: 'Customer', key: 'customerName' },
              { label: 'QuickBooks ID', key: 'quickBooksInvoiceId' },
              { label: 'Invoice Date', key: 'invoiceDate' },
              { label: 'Due Date', key: 'dueDate' },
              { label: 'Total', key: 'total' },
              { label: 'Remaining Balance', key: 'remainingBalance' },
            ]}
            filename={csvFileName}
          >
            <MdDownload />
            <span>Download</span>
          </CSVLink>
        </button>
      </Header>
      <ReceivableTable />
    </>
  );
}

export default OutstandingReceivablesBoard;
